/**
 * Modal Prise de besoin - Plan budgétaire 5 ans
 * Cloné depuis l'onglet "Plan budgétaire 5 ans" de la tuile Ambioner (Prisme_Ambioner).
 * Saisie des montants par poste et par année, projets en cours, améliorations faites/suggérées.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Plus, Trash2, DollarSign } from 'lucide-react';
import { createBesoin, updateBesoin, getBesoinById } from '../services/besoinsService';
import type { ClientItem } from '../services/clientService';
import { getAllClients } from '../services/clientService';

export interface PlanBudgetaire5AnsItem {
  id: string;
  label: string;
  y2026: string;
  y2027: string;
  y2028: string;
  y2029: string;
  y2030: string;
}

export interface PlanBudgetaire5AnsCategory {
  id: string;
  name: string;
  items: PlanBudgetaire5AnsItem[];
}

const DEFAULT_PLAN_5ANS: PlanBudgetaire5AnsCategory[] = [
  {
    id: 'pb5-infra',
    name: 'Matériel infrastructure serveur',
    items: [
      { id: 'pb5-i1', label: 'UPS', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i2', label: 'Serveur', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i3', label: 'Qnap', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i4', label: 'Pare-feux', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i5', label: 'Renouvellement services Pare-feux', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i6', label: 'Mini disques pour Backup', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i7', label: 'Switch 24 ports POE', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i8', label: 'Bornes Sans-fil (5 par année)', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
    ],
  },
  {
    id: 'pb5-logiciels',
    name: 'Logiciels et licences',
    items: [
      { id: 'pb5-i9', label: 'VMWARE', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i10', label: 'Microsoft 365', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i11', label: 'Sauvegardes 365', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i12', label: 'DUO', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i13', label: 'Veeam et Backup BZ', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i14', label: 'Acrobat', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i15', label: 'Antidote', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i16', label: 'Logiciels tiers', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
    ],
  },
  {
    id: 'pb5-services',
    name: 'Services TI',
    items: [
      { id: 'pb5-i17', label: 'Support BZ', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i18', label: 'Programmation', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i19', label: 'Cybersécurité Holistique', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i20', label: 'Formation', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i21', label: 'Téléphonie', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i22', label: "Solutions d'affaire", y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
    ],
  },
  {
    id: 'pb5-postes',
    name: 'Postes et autre équipement informatique',
    items: [
      { id: 'pb5-i23', label: 'Postes de travail', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
      { id: 'pb5-i24', label: 'Autres', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' },
    ],
  },
];

function parseNum(s: string): number {
  const n = parseFloat(String(s).replace(/[^\d,.-]/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
}

function formatPrice(n: number): string {
  return n.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' $';
}

interface PriseDeBesoinPlanBudgetaire5AnsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
  templateId?: string;
  templateVersion?: string;
  besoinId?: number;
  onSuccess?: (besoinId: number) => void;
}

export default function PriseDeBesoinPlanBudgetaire5AnsModal({
  isOpen,
  onClose,
  clientId,
  templateId = 'plan-budgetaire-5ans-v1',
  templateVersion = '1.0.0',
  besoinId,
  onSuccess,
}: PriseDeBesoinPlanBudgetaire5AnsModalProps) {
  const [planBudgetaire5Ans, setPlanBudgetaire5Ans] = useState<PlanBudgetaire5AnsCategory[]>(DEFAULT_PLAN_5ANS);
  const [projetsEnCours, setProjetsEnCours] = useState('');
  const [ameliorationsFaites, setAmeliorationsFaites] = useState('');
  const [ameliorationsSuggerees, setAmeliorationsSuggerees] = useState('');
  const [nomBesoin, setNomBesoin] = useState('');
  const [descriptionBesoin, setDescriptionBesoin] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | ''>(clientId || '');
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && besoinId) {
      getBesoinById(besoinId).then((besoin) => {
        if (besoin) {
          setNomBesoin(besoin.nom || '');
          setDescriptionBesoin(besoin.description || '');
          setSelectedClientId(besoin.client_id ?? '');
          const phase1 = (besoin as any).phase1_data as Record<string, unknown> | undefined;
          if (phase1?.plan_budgetaire_5ans && Array.isArray(phase1.plan_budgetaire_5ans) && (phase1.plan_budgetaire_5ans as PlanBudgetaire5AnsCategory[]).length > 0) {
            setPlanBudgetaire5Ans(phase1.plan_budgetaire_5ans as PlanBudgetaire5AnsCategory[]);
          }
          if (typeof phase1?.projets_en_cours === 'string') setProjetsEnCours(phase1.projets_en_cours);
          if (typeof phase1?.ameliorations_faites === 'string') setAmeliorationsFaites(phase1.ameliorations_faites);
          if (typeof phase1?.ameliorations_suggerees === 'string') setAmeliorationsSuggerees(phase1.ameliorations_suggerees);
        }
      }).catch(() => {});
    } else if (isOpen && !besoinId) {
      setPlanBudgetaire5Ans(JSON.parse(JSON.stringify(DEFAULT_PLAN_5ANS)));
      setProjetsEnCours('');
      setAmeliorationsFaites('');
      setAmeliorationsSuggerees('');
      setNomBesoin('');
      setDescriptionBesoin('');
      setSelectedClientId(clientId ?? '');
    }
  }, [isOpen, besoinId, clientId]);

  useEffect(() => {
    if (isOpen) {
      getAllClients().then(setClients).catch(() => setClients([]));
    }
  }, [isOpen]);

  const updatePlan5AnsItem = (catId: string, itemId: string, field: keyof PlanBudgetaire5AnsItem, value: string) => {
    if (field === 'label') {
      setPlanBudgetaire5Ans((prev) =>
        prev.map((c) =>
          c.id === catId ? { ...c, items: c.items.map((i) => (i.id === itemId ? { ...i, label: value } : i)) } : c
        )
      );
    } else if (['y2026', 'y2027', 'y2028', 'y2029', 'y2030'].includes(field)) {
      setPlanBudgetaire5Ans((prev) =>
        prev.map((c) =>
          c.id === catId ? { ...c, items: c.items.map((i) => (i.id === itemId ? { ...i, [field]: value } : i)) } : c
        )
      );
    }
  };

  const addPlan5AnsItem = (catId: string) => {
    setPlanBudgetaire5Ans((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              items: [...c.items, { id: `pb5-item-${Date.now()}`, label: '', y2026: '', y2027: '', y2028: '', y2029: '', y2030: '' }],
            }
          : c
      )
    );
  };

  const removePlan5AnsItem = (catId: string, itemId: string) => {
    setPlanBudgetaire5Ans((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, items: c.items.filter((i) => i.id !== itemId) } : c))
    );
  };

  const handleSave = async () => {
    const cId = selectedClientId === '' ? clientId : selectedClientId;
    if (cId == null) return;
    setIsSaving(true);
    try {
      const phase1_data = {
        plan_budgetaire_5ans: planBudgetaire5Ans,
        projets_en_cours: projetsEnCours,
        ameliorations_faites: ameliorationsFaites,
        ameliorations_suggerees: ameliorationsSuggerees,
      };
      if (besoinId) {
        await updateBesoin(besoinId, {
          nom: nomBesoin || 'Plan budgétaire 5 ans',
          description: descriptionBesoin,
          client_id: cId as number,
          template_id: templateId,
          template_version: templateVersion,
          phase1_data,
        });
        onSuccess?.(besoinId);
      } else {
        const created = await createBesoin({
          client_id: cId as number,
          template_id: templateId,
          template_version: templateVersion,
          nom: nomBesoin || 'Plan budgétaire 5 ans',
          description: descriptionBesoin,
          phase1_data,
        });
        onSuccess?.(created.id!);
      }
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-[95vw] max-w-[1600px] max-h-[90vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Prise de besoin - Plan budgétaire 5 ans</h2>
                <p className="text-sm text-white/90">Montants par poste et par année, projets en cours, améliorations</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/20 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nom du formulaire</label>
                <input
                  type="text"
                  value={nomBesoin}
                  onChange={(e) => setNomBesoin(e.target.value)}
                  placeholder="Ex: Plan budgétaire 5 ans - Client X"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Client</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description (optionnel)</label>
              <input
                type="text"
                value={descriptionBesoin}
                onChange={(e) => setDescriptionBesoin(e.target.value)}
                placeholder="Description courte"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="bg-indigo-50 p-3 rounded-lg">
              <p className="text-sm text-indigo-800">
                <strong>Plan budgétaire vision 5 ans</strong> — Basé sur le modèle Plan directeur TI. Saisissez les montants par poste et par année. Les totaux sont calculés automatiquement.
              </p>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-300 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Projets en cours</label>
                <textarea
                  value={projetsEnCours}
                  onChange={(e) => setProjetsEnCours(e.target.value)}
                  placeholder="Saisissez les projets en cours..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="border border-gray-300 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Améliorations faites</label>
                <textarea
                  value={ameliorationsFaites}
                  onChange={(e) => setAmeliorationsFaites(e.target.value)}
                  placeholder="Saisissez les améliorations faites..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="border border-gray-300 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Améliorations suggérées</label>
                <textarea
                  value={ameliorationsSuggerees}
                  onChange={(e) => setAmeliorationsSuggerees(e.target.value)}
                  placeholder="Saisissez les améliorations suggérées..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border border-gray-300 min-w-[1200px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-2 text-left font-bold w-80">Poste</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold">2026</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold">2027</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold">2028</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold">2029</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold">2030</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold">Total 5 ans</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold w-12">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {planBudgetaire5Ans.map((cat) => {
                    let catTotal2026 = 0, catTotal2027 = 0, catTotal2028 = 0, catTotal2029 = 0, catTotal2030 = 0;
                    return (
                      <React.Fragment key={cat.id}>
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="border border-gray-300 px-3 py-2 font-semibold text-gray-800">
                            <div className="flex items-center justify-between">
                              <span>{cat.name}</span>
                              <button
                                type="button"
                                onClick={() => addPlan5AnsItem(cat.id)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
                              >
                                <Plus className="w-3 h-3" /> Ajouter
                              </button>
                            </div>
                          </td>
                        </tr>
                        {cat.items.map((it) => {
                          const v2026 = parseNum(it.y2026), v2027 = parseNum(it.y2027), v2028 = parseNum(it.y2028), v2029 = parseNum(it.y2029), v2030 = parseNum(it.y2030);
                          const rowTotal = v2026 + v2027 + v2028 + v2029 + v2030;
                          catTotal2026 += v2026; catTotal2027 += v2027; catTotal2028 += v2028; catTotal2029 += v2029; catTotal2030 += v2030;
                          return (
                            <tr key={it.id} className="hover:bg-gray-50/50">
                              <td className="border border-gray-300 p-0">
                                <input
                                  type="text"
                                  value={it.label}
                                  onChange={(e) => updatePlan5AnsItem(cat.id, it.id, 'label', e.target.value)}
                                  placeholder="Nom du poste"
                                  className="w-full px-2 py-1 border-0 text-left focus:ring-1 focus:ring-indigo-500"
                                />
                              </td>
                              {(['y2026', 'y2027', 'y2028', 'y2029', 'y2030'] as const).map((field) => (
                                <td key={field} className="border border-gray-300 p-0">
                                  <input
                                    type="text"
                                    value={it[field]}
                                    onChange={(e) => updatePlan5AnsItem(cat.id, it.id, field, e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1 border-0 text-right focus:ring-1 focus:ring-indigo-500"
                                  />
                                </td>
                              ))}
                              <td className="border border-gray-300 px-2 py-1.5 text-right font-medium">{formatPrice(rowTotal)}</td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => removePlan5AnsItem(cat.id, it.id)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-gray-100 font-semibold">
                          <td className="border border-gray-300 px-2 py-1.5">Sous-total {cat.name}</td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right">{formatPrice(catTotal2026)}</td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right">{formatPrice(catTotal2027)}</td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right">{formatPrice(catTotal2028)}</td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right">{formatPrice(catTotal2029)}</td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right">{formatPrice(catTotal2030)}</td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right">{formatPrice(catTotal2026 + catTotal2027 + catTotal2028 + catTotal2029 + catTotal2030)}</td>
                          <td className="border border-gray-300 px-2 py-1.5" />
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  <tr className="bg-indigo-100 font-bold">
                    <td className="border border-gray-300 px-3 py-2">Budget annuel</td>
                    <td className="border border-gray-300 px-2 py-2 text-right">
                      {formatPrice(planBudgetaire5Ans.reduce((s, c) => s + c.items.reduce((a, i) => a + parseNum(i.y2026), 0), 0))}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-right">
                      {formatPrice(planBudgetaire5Ans.reduce((s, c) => s + c.items.reduce((a, i) => a + parseNum(i.y2027), 0), 0))}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-right">
                      {formatPrice(planBudgetaire5Ans.reduce((s, c) => s + c.items.reduce((a, i) => a + parseNum(i.y2028), 0), 0))}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-right">
                      {formatPrice(planBudgetaire5Ans.reduce((s, c) => s + c.items.reduce((a, i) => a + parseNum(i.y2029), 0), 0))}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-right">
                      {formatPrice(planBudgetaire5Ans.reduce((s, c) => s + c.items.reduce((a, i) => a + parseNum(i.y2030), 0), 0))}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-right">
                      {formatPrice(planBudgetaire5Ans.reduce((s, c) => s + c.items.reduce((a, i) => a + parseNum(i.y2026) + parseNum(i.y2027) + parseNum(i.y2028) + parseNum(i.y2029) + parseNum(i.y2030), 0), 0))}
                    </td>
                    <td className="border border-gray-300 px-2 py-2" />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 font-medium"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || selectedClientId === ''}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
